import * as XLSX from "xlsx";
import { postRawData } from "../../helpers/url-request";

export const newQuestion = (question = "", section = "General", index = 0) => ({
  key: `q-${index}`,
  section,
  referenceNo: `Q${String(index + 1).padStart(2, "0")}`,
  question,
  answerType: "None",
  required: false,
  supporting: "",
  instructions: "",
  references: "",
  explanation: "",
  needsCheck: true,
});

export function parseChecklistText(text) {
  let section = "General";
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .reduce((questions, line) => {
      if (/^#{1,6}\s/.test(line) || /^\[.+\]$/.test(line)) {
        section = line.replace(/^#{1,6}\s+|^\[|\]$/g, "").trim();
      } else {
        questions.push(
          newQuestion(
            line.replace(/^[-*]\s+|^\d+[.)]\s+/, ""),
            section,
            questions.length
          )
        );
      }
      return questions;
    }, []);
}

export async function readChecklistFile(file) {
  if (file.size > 10 * 1024 * 1024)
    throw new Error("Choose a file smaller than 10 MB.");
  const extension = file.name.split(".").pop().toLowerCase();
  if (extension === "pdf") return (await readChecklistDocument(file)).questions;
  let questions;
  if (["txt", "md"].includes(extension))
    questions = parseChecklistText(await file.text());
  else if (["csv", "xlsx", "xls"].includes(extension)) {
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
    questions = workbook.SheetNames.flatMap((name) =>
      XLSX.utils
        .sheet_to_json(workbook.Sheets[name], { defval: "" })
        .map((row) => {
          const normalized = Object.fromEntries(
            Object.entries(row).map(([key, value]) => [
              key.toLowerCase().replace(/[^a-z]/g, ""),
              String(value).trim(),
            ])
          );
          if (!normalized.question)
            throw new Error(
              "Each spreadsheet row must have a Question column."
            );
          return {
            ...newQuestion(normalized.question, normalized.section || name),
            referenceNo: normalized.questionid || normalized.referenceno || "",
            supporting: normalized.supportingrequirements || "",
            instructions: normalized.instructions || "",
            references: normalized.references || "",
            answerType: [
              "None",
              "Yes / No / N/A",
              "Long text",
              "Short text",
              "Number",
              "Date",
            ].includes(normalized.answertype)
              ? normalized.answertype
              : "None",
            required: false,
          };
        })
    );
  } else throw new Error("Supported files: PDF, Excel, CSV, TXT and Markdown.");
  if (!questions.length) throw new Error("No questions found in this file.");
  if (questions.length > 1000)
    throw new Error("Import up to 1,000 questions at a time.");
  return questions.map((question, index) => ({
    ...question,
    key: `q-${index}`,
    referenceNo:
      question.referenceNo || `Q${String(index + 1).padStart(2, "0")}`,
  }));
}

export async function readChecklistDocument(file) {
  if (file.size > 10 * 1024 * 1024)
    throw new Error("Choose a file smaller than 10 MB.");
  if (!file.name.toLowerCase().endsWith(".pdf"))
    return { questions: await readChecklistFile(file) };
  const form = new FormData();
  form.append("file", file);
  const response = await postRawData("/checklist/extract-pdf", "POST", form);
  if (!response.status || !response.data?.questions?.length)
    throw new Error(response.message || "No questions found in this PDF.");
  const data = response.data;
  return {
    ...data,
    questions: data.questions.map((question, index) => ({
      ...newQuestion(question.question, question.section, index),
      ...question,
      answerType: "None",
      required: false,
      key: `q-${index}`,
      needsCheck: true,
    })),
  };
}

export function questionDescription(question) {
  return [
    `Answer type: ${question.answerType}`,
    `Required: ${question.required ? "Yes" : "No"}`,
    question.supporting && `Supporting requirements:\n${question.supporting}`,
    question.instructions && `Instructions:\n${question.instructions}`,
    question.references && `References: ${question.references}`,
    question.explanation && `Explanation rule: ${question.explanation}`,
    question.sourcePage &&
      `Source: PDF page ${question.sourcePage}${
        question.sourceEndPage > question.sourcePage
          ? `–${question.sourceEndPage}`
          : ""
      }`,
  ]
    .filter(Boolean)
    .join("\n\n");
}

// Match document evidence against this firm's catalogue; ambiguous matches stay unset.
export function matchChecklistStandard(document, standards = []) {
  const normalize = (value) =>
    String(value || "")
      .normalize("NFKC")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const text = ` ${normalize(
    [
      document.standard,
      document.standardName,
      document.name,
      document.sourceText,
    ]
      .filter(Boolean)
      .join(" ")
  )} `;
  const matches = standards.filter((standard) => {
    const name = normalize(standard.name);
    if (!name) return false;
    const aliases = [name];
    if (
      name === "aicpa" ||
      name === "american institute of certified public accountants"
    )
      aliases.push(
        "aicpa",
        "american institute of certified public accountants"
      );
    return aliases.some((alias) => text.includes(` ${alias} `));
  });
  return matches.length === 1 ? String(matches[0].id) : "";
}
